import { useCallback, useEffect, useState } from "react";
import { Bookmark, Link2, X } from "lucide-react";
import { api } from "../lib/api";
import { readSelection, writeSelection } from "../lib/selections";

const STORE = "kskf-faceoff-saved";

const read = () => readSelection(STORE, []);

/** The picks that make a face-off, in a stable order, so the same comparison is never saved twice. */
export const faceOffId = (s) =>
  [s.mode, s.mode === "counties" ? s.facility : "", s.a, s.b, s.mode === "counties" ? "" : s.county,
    s.funding, s.acres || ""].join("|");

export const faceOffQuery = (s) =>
  new URLSearchParams(
    s.mode === "counties"
      ? { mode: "counties", facility: s.facility, a: s.a, b: s.b, funding: s.funding, ...(s.acres ? { acres: s.acres } : {}) }
      : { a: s.a, b: s.b, county: s.county, funding: s.funding, ...(s.acres ? { acres: s.acres } : {}) },
  ).toString();

const usd = (n) => (n ? `$${Math.round(n).toLocaleString()}` : "—");
const yrs = (n) => (n ? `${n} yrs` : "—");
const pct = (n) => (n === null || n === undefined ? "—" : `${n}%`);

/**
 * A shortlist of face-offs, kept for this visit only. Every saved line is re-priced from the live model when
 * the page loads, so a comparison you come back to is never quoting yesterday's numbers.
 */
export const SavedFaceOffs = ({ current, onOpen }) => {
  const [saved, setSaved] = useState(read);
  const [rows, setRows] = useState({});
  const [copied, setCopied] = useState("");

  const persist = (next) => {
    setSaved(next);
    writeSelection(STORE, next);
  };

  const load = useCallback(async (s) => {
    const path = s.mode === "counties" ? "county-face-off" : "facility-face-off";
    try {
      const r = await api.get(`/${path}?${faceOffQuery(s)}`);
      const d = r.data;
      setRows((prev) => ({
        ...prev,
        [faceOffId(s)]: {
          title: s.mode === "counties"
            ? `${d.facility?.name || ""} · ${d.a.county} v ${d.b.county}`
            : `${d.a.name} v ${d.b.name}`,
          sub: s.mode === "counties"
            ? `${d.a.funding_label || ""}${s.acres ? ` · ${s.acres} acres` : ""}`
            : `${d.county}${s.acres ? ` · ${s.acres} acres` : ""}`,
          capex: `${usd(d.a.capex_usd)} / ${usd(d.b.capex_usd)}`,
          payback: `${yrs(d.a.payback_years)} / ${yrs(d.b.payback_years)}`,
          roce: `${pct(d.a.roce_pct)} / ${pct(d.b.roce_pct)}`,
          money: d.money_case?.text || "—",
          reach: d.reach_case?.text || "—",
        },
      }));
    } catch {
      // a saved line that cannot be priced simply shows dashes
    }
  }, []);

  useEffect(() => { saved.forEach(load); }, [saved, load]);

  const isSaved = saved.some((s) => faceOffId(s) === faceOffId(current));

  const copy = (s) => {
    const url = `${window.location.origin}/compare-facilities?${faceOffQuery(s)}`;
    try { navigator.clipboard.writeText(url); } catch { /* clipboard blocked */ }
    setCopied(faceOffId(s));
    setTimeout(() => setCopied(""), 2200);
  };

  return (
    <section className="mt-20" data-testid="saved-face-offs">
      <p className="eyebrow">Your shortlist</p>
      <h2 className="display-3 mt-4">Keep A Face-Off To Come Back To</h2>
      <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
        Save any comparison and it stays for this visit. Each saved line is re-priced from the live model when
        this page loads, and the link reopens the exact same face-off for anyone you send it to.
      </p>

      <button
        type="button"
        onClick={() => persist(isSaved ? saved.filter((s) => faceOffId(s) !== faceOffId(current)) : [...saved, current])}
        data-testid="saved-face-offs-save"
        className={`mono mt-7 inline-flex items-center gap-2 border px-5 py-3 text-[0.6rem] uppercase tracking-[0.16em] transition-colors ${
          isSaved ? "border-ochre bg-ochre text-black" : "border-ochre text-ochre hover:bg-ochre hover:text-black"
        }`}
      >
        <Bookmark size={13} /> {isSaved ? "Saved — tap to remove" : "Save this face-off"}
      </button>

      {saved.length ? (
        <div className="mt-8 overflow-x-auto border border-zinc-900">
          <table className="w-full min-w-[980px] text-left" data-testid="saved-face-offs-table">
            <thead>
              <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                {["Saved face-off", "Build cost", "Yrs to match cost", "Surplus share", "On the money", "On reach", ""].map((h) => (
                  <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {saved.map((s) => {
                const id = faceOffId(s);
                const r = rows[id] || {};
                const testId = id.split("|").filter(Boolean).join("-").toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <tr key={id} className="border-b border-zinc-900" data-testid={`saved-face-off-row-${testId}`}>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => onOpen(s)}
                        data-testid={`saved-face-off-open-${testId}`}
                        className="text-left font-display text-sm uppercase tracking-tight text-white hover:text-ochre"
                      >
                        {r.title || `${s.a} v ${s.b}`}
                      </button>
                      <span className="mono mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-400">
                        {r.sub || (s.mode === "counties" ? "Two counties" : s.county)}
                      </span>
                    </td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{r.capex || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{r.payback || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{r.roce || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-ochre">{r.money || "—"}</td>
                    <td className="mono px-4 py-4 text-xs text-ochre">{r.reach || "—"}</td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => copy(s)}
                          data-testid={`saved-face-off-copy-${testId}`}
                          className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-500 transition-colors hover:text-ochre"
                          aria-label="Copy a link to this face-off"
                        >
                          {copied === id ? "Copied" : <Link2 size={14} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => persist(saved.filter((x) => faceOffId(x) !== id))}
                          data-testid={`saved-face-off-remove-${testId}`}
                          className="text-zinc-500 transition-colors hover:text-maasai"
                          aria-label="Remove this saved face-off"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="mono mt-8 max-w-2xl border-l-2 border-ochre bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-ochre"
          data-testid="saved-face-offs-empty">
          Nothing saved yet. Set a comparison above, then save it and build a shortlist.
        </p>
      )}
    </section>
  );
};

export default SavedFaceOffs;
